import { Page, Button, EditIcon } from '@occc-app/design-system';
import { RetailerNetworkFragment, RetailerNetworkLocationDataFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkLocations } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { LocationAddressGeolocationCard } from './components/location-address-geolocation-card';
import { LocationGeneralInfoCardContainer as LocationGeneralInfoCard } from './components/location-general-info-card';
import LocationUsersTable from './components/location-users-table';

import { UseSetLocationSpecificGoalsReturnType } from '../components/retailer-network-performance/hooks/use-set-location-specific-goals.hook';
import RetailerNetworkMonthlyGoals from '../components/retailer-network-monthly-goals/retailer-network-monthly-goals.container';
import RetailerNetworkPerformance from '../components/retailer-network-performance';
import { StoreBrandingCard } from '../retailer-network-my-stores-store-v2-page/components/store-branding-card.component';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';

const PAGE_TITLE = 'Retailer Network Location Details';

type RetailerNetworkLocationDetailsPageProps = {
  location: RetailerNetworkLocationDataFragment;
  retailerNetwork: RetailerNetworkFragment;
  navigateToEditLocation: () => void;
  setLocationPerformanceGoals: UseSetLocationSpecificGoalsReturnType;
  isSpecificGoalSet?: boolean;
};

export const RetailerNetworkLocationDetailsPage = ({
  location,
  retailerNetwork,
  isSpecificGoalSet,
  navigateToEditLocation,
  setLocationPerformanceGoals,
}: RetailerNetworkLocationDetailsPageProps) => {
  const { isRetailerNetworkSiteUser } = useRetailerNetworkUserSelector();
  const geoLocation = location.address?.geoLocation || null;
  const locationName = location?.name || location?.siteSystemConfiguration?.[0]?.identifier || '';

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: locationName,
        subtitle: location?.name ? location?.siteSystemConfiguration?.[0]?.identifier : '',
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <Can I={UpdateRetailerNetworkLocations({ uuid: retailerNetwork.uuid })}>
          <Button
            variant='contained'
            color='secondary'
            startIcon={<EditIcon />}
            role='button'
            onClick={navigateToEditLocation}
          >
            Edit
          </Button>
        </Can>
      }
    >
      <LocationGeneralInfoCard location={location} />

      <RetailerNetworkMonthlyGoals
        filter={{
          locationUuidEquals: location.uuid,
          retailerNetworkUuidEquals: retailerNetwork.uuid,
        }}
      />

      <RetailerNetworkPerformance
        title='Location Performance'
        filter={{
          retailerNetworkUuidEquals: retailerNetwork.uuid,
          locationUuidEquals: location.uuid,
        }}
        setSpecificGoal={setLocationPerformanceGoals}
        isSpecificGoalSet={isSpecificGoalSet}
        scope='location'
        retailerNetworkGoals={{
          membersPerStore: retailerNetwork.monthlyPerformanceGoal.membersPerStore,
          transactionsPerStore: retailerNetwork.monthlyPerformanceGoal.transactionsPerStore,
        }}
      />

      <LocationAddressGeolocationCard geoLocation={geoLocation} hasAddress={!!location.address} />

      <StoreBrandingCard brandingDetails={location.brandingDetails} />

      {!isRetailerNetworkSiteUser && (
        <LocationUsersTable retailerNetworkUuid={retailerNetwork.uuid} location={location} />
      )}
    </Page>
  );
};
